import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Eye, LogIn } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useUserLeagues } from '../contexts/UserLeaguesContext';
import { useHomeSummary } from '../contexts/HomeSummaryContext';
import { joinLeague, syncLabel } from '../utils/joinLeague';
import { relativeTime } from '../utils/formatting';
import { Panel } from '../components/ledger';
import { SidebarLayout } from '../layouts/SidebarLayout';
import { HomeLeagueCard } from '../components/home/HomeLeagueCard';
import { HomeAddLeagueCard } from '../components/home/HomeAddLeagueCard';
import { HomeConnectTile } from '../components/home/HomeConnectTile';
import { LegalLinks } from '../components/home/LegalLinks';

const primaryBtnStyle = { background: 'var(--ledger-ink)', borderColor: 'var(--ledger-ink)', color: 'var(--ledger-panel)' };

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--ledger-bg)' }}>
      <div className="relative z-10 w-full max-w-md ledger-scope">
        {children}
      </div>
    </div>
  );
}

// Home (spec 2026-09-23 ledger home): signed out, the landing card; signed in,
// the Ledger front door inside the app shell — one card per added league from
// GET /me/home, an Add card per discovered league not added yet, and the
// connect tile. It no longer redirects into the first league.
export function HomePage() {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { leagues: contextLeagues } = useUserLeagues();
  const { summary, error: summaryError, loading: summaryLoading, refetch: refetchSummary } = useHomeSummary();
  const navigate = useNavigate();
  const [syncingLeagueId, setSyncingLeagueId] = useState(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncText, setSyncText] = useState('');
  const [error, setError] = useState('');

  // Adding a discovered league: the existing join flow (join, sync, then open it).
  const handleAdd = useCallback(async (leagueId) => {
    setError('');
    setSyncingLeagueId(leagueId);
    setSyncProgress(0);
    await joinLeague({
      leagueId, getToken, navigate,
      onUpdate: (update) => {
        if (update.status === 'syncing') { setSyncProgress(update.progress); setSyncText(syncLabel(update)); }
        else if (update.status === 'error') { setSyncingLeagueId(null); setSyncProgress(0); setSyncText(''); setError(update.message); }
      },
    });
    setSyncingLeagueId(null);
  }, [getToken, navigate]);

  // ── Signed-out landing page ──
  if (isLoaded && !isSignedIn) {
    return (
      <Shell>
        <Panel className="overflow-hidden">
          <div className="px-6 pt-8 pb-6 text-center" style={{ borderBottom: '1px solid var(--ledger-line)' }}>
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--ledger-ink)', letterSpacing: '-0.5px' }}>IceIQ</h1>
            <p className="text-sm" style={{ color: 'var(--ledger-muted)' }}>Fantasy Analytics</p>
          </div>
          <div className="p-6 space-y-3">
            <Link
              to="/sign-in"
              className="w-full flex items-center justify-center gap-2 ledger-btn"
              style={primaryBtnStyle}
            >
              <LogIn className="w-4 h-4" /> Sign In
            </Link>
            <Link
              to="/sign-up"
              className="w-full flex items-center justify-center gap-2 ledger-btn"
            >
              Create Account
            </Link>
            <Link
              to="/example"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium"
              style={{ color: 'var(--ledger-muted)' }}
            >
              <Eye className="w-4 h-4" /> View Example Dashboard
            </Link>
          </div>
        </Panel>
        <LegalLinks style={{ marginTop: '16px' }} />
      </Shell>
    );
  }

  if (!isLoaded) {
    return (
      <Shell>
        <Panel bodyClassName="p-10 text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-5 animate-spin" style={{ color: 'var(--ledger-muted)' }} />
          <div className="text-lg font-bold mb-1" style={{ color: 'var(--ledger-ink)' }}>IceIQ</div>
        </Panel>
      </Shell>
    );
  }

  const added = summary?.leagues ?? [];
  const addedIds = new Set(added.map(l => String(l.league_id)));
  const discovered = (Array.isArray(contextLeagues) ? contextLeagues : []).filter(l => !addedIds.has(String(l.league_id)));
  const synced = relativeTime(summary?.last_synced_at);
  // Until /me/home has answered (or failed), "0 leagues connected" would be a lie
  // that flips to your cards a moment later: say it's loading and hold the grid.
  const pending = !summary && !summaryError;
  const sub = [`${added.length} league${added.length === 1 ? '' : 's'} connected`, synced ? `last synced ${synced}` : null].filter(Boolean).join(' · ');

  return (
    <SidebarLayout isHome tabs={[]} leagueName="Home" headMeta="All your leagues" isLoading={summaryLoading} onRefresh={refetchSummary}>
      <div className="home-page ledger-scope">
        <h1 className="home-greet">{pending || added.length ? 'Welcome back' : 'Welcome'}</h1>
        <p className="home-sub">{summaryError ? `Couldn't load your leagues: ${summaryError}` : pending ? 'Loading your leagues…' : sub}</p>
        {error && <p className="ledger-state is-bad is-wrap" style={{ marginBottom: '14px' }}>{error}</p>}
        {!pending && (
          <div className="home-grid">
            {added.map(league => (
              <HomeLeagueCard
                key={league.league_id}
                league={league}
                onRetry={refetchSummary}
                onSync={() => handleAdd(league.league_id)}
                syncing={syncingLeagueId === league.league_id}
                progress={syncProgress}
                label={syncText}
              />
            ))}
            {discovered.map(league => (
              <HomeAddLeagueCard
                key={league.league_id}
                league={league}
                syncing={syncingLeagueId === league.league_id}
                progress={syncProgress}
                label={syncText}
                disabled={syncingLeagueId != null}
                onAdd={() => handleAdd(league.league_id)}
              />
            ))}
            <HomeConnectTile />
          </div>
        )}
        <LegalLinks style={{ marginTop: '28px', paddingTop: '14px', borderTop: '1px solid var(--ledger-line)' }} />
      </div>
    </SidebarLayout>
  );
}

export default HomePage;
